import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Plus, FileImage, X, Upload, Link } from "lucide-react";

const contentTypes = [
  { value: "ad", label: "Ad" },
  { value: "landing_page", label: "Landing Page" },
  { value: "ugc", label: "UGC" },
  { value: "email", label: "Email" },
];

const platforms = ["Facebook", "Instagram", "TikTok", "LinkedIn", "YouTube", "Email", "Web", "Other"];

const AddDropdown = ({ categories, onRefresh }) => {
  const { api } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const ref = useRef(null);

  // Content form state
  const [contentForm, setContentForm] = useState({
    title: "", description: "", image_url: "", source_url: "",
    content_type: "ad", platforms: [], tags: "", category_id: "", initial_note: "",
  });
  const [saving, setSaving] = useState(false);
  const [imageMode, setImageMode] = useState("url"); // "url" | "upload"
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  const resetForm = () => {
    setContentForm({
      title: "", description: "", image_url: "", source_url: "",
      content_type: "ad", platforms: [], tags: "", category_id: "", initial_note: "",
    });
    setImageMode("url");
    setImagePreview("");
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/content/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      updateContent("image_url", res.data.url);
      setImagePreview(res.data.preview_url || res.data.url);
    } catch (err) {
      console.error("Upload failed", err);
    } finally {
      setUploading(false);
    }
  };

  const togglePlatform = (platform) => {
    setContentForm(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  const handleAddContent = async (e) => {
    e.preventDefault();
    if (!contentForm.title.trim()) return;
    setSaving(true);
    try {
      // Create content with platforms as array
      const res = await api.post("/content", {
        title: contentForm.title,
        description: contentForm.description,
        image_url: contentForm.image_url,
        source_url: contentForm.source_url,
        content_type: contentForm.content_type,
        platform: contentForm.platforms.join(", "), // Store as comma-separated for backward compatibility
        platforms: contentForm.platforms,
        tags: contentForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
        category_id: contentForm.category_id || null,
      });
      
      // If there's an initial note, create it
      if (contentForm.initial_note.trim()) {
        await api.post(`/content/${res.data.id}/notes`, { text: contentForm.initial_note.trim() });
      }
      
      onRefresh?.();
      setShowModal(false);
      resetForm();
    } catch (err) {
      console.error("Error creating content:", err);
    } finally { 
      setSaving(false); 
    }
  };

  const updateContent = (key, val) => setContentForm((prev) => ({ ...prev, [key]: val }));

  return (
    <>
      <div ref={ref} className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-4 py-2 bg-white text-black text-[13px] font-medium rounded-lg transition-all duration-200 hover:shadow-[0_0_15px_rgba(255,255,255,0.08)]"
          data-testid="add-dropdown-btn"
        >
          <Plus size={15} />
          Ajouter
        </button>

        {isOpen && (
          <div 
            className="absolute top-full right-0 mt-1.5 bg-[#111114] border border-white/[0.08] rounded-xl shadow-2xl z-50 min-w-[200px] py-1.5 overflow-hidden"
            style={{ animation: "fadeIn 0.15s ease-out" }}
          >
            <button
              onClick={() => { setShowModal(true); setIsOpen(false); }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-[13px] text-[#999] hover:text-white hover:bg-white/[0.04] transition-colors"
              data-testid="add-content-option"
            >
              <FileImage size={15} className="text-blue-400" />
              Ajouter un contenu
            </button>
          </div>
        )}
      </div>

      {/* Content Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" onClick={() => setShowModal(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" />
          <div
            className="relative bg-[#0c0c0e] border border-white/[0.08] rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "fadeIn 0.2s ease-out" }}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
              <h3 className="text-white font-semibold text-sm">Ajouter un contenu</h3>
              <button onClick={() => setShowModal(false)} className="text-[#555] hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleAddContent} className="p-6 space-y-4">
              <div>
                <label className="text-[11px] text-[#555] uppercase tracking-widest font-medium mb-1.5 block">Titre *</label>
                <input
                  value={contentForm.title}
                  onChange={(e) => updateContent("title", e.target.value)}
                  placeholder="Nike - Just Do It Campaign"
                  className="w-full bg-white/[0.03] border border-white/[0.07] rounded-lg px-3.5 py-2.5 text-[13px] text-white placeholder:text-[#333] focus:outline-none focus:border-white/[0.15] transition-colors"
                  required
                  data-testid="content-title-input"
                />
              </div>

              <div>
                <label className="text-[11px] text-[#555] uppercase tracking-widest font-medium mb-1.5 block">Type</label>
                <select
                  value={contentForm.content_type}
                  onChange={(e) => updateContent("content_type", e.target.value)}
                  className="w-full bg-white/[0.03] border border-white/[0.07] rounded-lg px-3.5 py-2.5 text-[13px] text-white focus:outline-none focus:border-white/[0.15] transition-colors appearance-none"
                  data-testid="content-type-select"
                >
                  {contentTypes.map((t) => <option key={t.value} value={t.value} className="bg-[#111] text-white">{t.label}</option>)}
                </select>
              </div>

              <div>
                <label className="text-[11px] text-[#555] uppercase tracking-widest font-medium mb-1.5 block">Plateformes</label>
                <div className="flex items-center gap-2 flex-wrap">
                  {platforms.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => togglePlatform(p)}
                      className={`px-3 py-1.5 rounded-lg text-[12px] border transition-all ${
                        contentForm.platforms.includes(p) 
                          ? "border-white/[0.15] bg-white/[0.08] text-white" 
                          : "border-white/[0.06] text-[#555] hover:text-white hover:border-white/[0.1]"
                      }`}
                      data-testid={`platform-${p.toLowerCase()}`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                {contentForm.platforms.length > 0 && (
                  <p className="text-[11px] text-[#555] mt-1.5">
                    Sélectionné : {contentForm.platforms.join(", ")}
                  </p>
                )}
              </div>

              <div>
                <label className="text-[11px] text-[#555] uppercase tracking-widest font-medium mb-1.5 block">Description</label>
                <textarea
                  value={contentForm.description}
                  onChange={(e) => updateContent("description", e.target.value)}
                  placeholder="Brief description of the content..."
                  rows={2}
                  className="w-full bg-white/[0.03] border border-white/[0.07] rounded-lg px-3.5 py-2.5 text-[13px] text-white placeholder:text-[#333] focus:outline-none focus:border-white/[0.15] transition-colors resize-none"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[11px] text-[#555] uppercase tracking-widest font-medium">Image</label>
                  <div className="flex items-center bg-white/[0.04] rounded-lg p-0.5 border border-white/[0.06]">
                    <button
                      type="button"
                      onClick={() => setImageMode("url")}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] transition-all ${imageMode === "url" ? "bg-white/[0.1] text-white" : "text-[#555] hover:text-white"}`}
                    >
                      <Link size={11} /> URL
                    </button>
                    <button
                      type="button"
                      onClick={() => setImageMode("upload")}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] transition-all ${imageMode === "upload" ? "bg-white/[0.1] text-white" : "text-[#555] hover:text-white"}`}
                    >
                      <Upload size={11} /> Upload
                    </button>
                  </div>
                </div>

                {imageMode === "url" ? (
                  <input
                    value={contentForm.image_url}
                    onChange={(e) => { updateContent("image_url", e.target.value); setImagePreview(""); }}
                    placeholder="https://..."
                    className="w-full bg-white/[0.03] border border-white/[0.07] rounded-lg px-3.5 py-2.5 text-[13px] text-white placeholder:text-[#333] focus:outline-none focus:border-white/[0.15] transition-colors"
                  />
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full bg-white/[0.03] border border-dashed border-white/[0.12] rounded-lg px-3.5 py-5 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-white/[0.25] hover:bg-white/[0.05] transition-all"
                  >
                    {imagePreview ? (
                      <img src={imagePreview} alt="preview" className="max-h-28 rounded-lg object-contain" />
                    ) : uploading ? (
                      <span className="text-[12px] text-[#555]">Upload en cours…</span>
                    ) : (
                      <>
                        <Upload size={20} className="text-[#444]" />
                        <span className="text-[12px] text-[#555]">Cliquer pour choisir un fichier</span>
                        <span className="text-[10px] text-[#333]">JPG, PNG, GIF, WebP — max 10MB</span>
                      </>
                    )}
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </div>

              <div>
                <label className="text-[11px] text-[#555] uppercase tracking-widest font-medium mb-1.5 block">URL source</label>
                <input
                  value={contentForm.source_url}
                  onChange={(e) => updateContent("source_url", e.target.value)}
                  placeholder="https://facebook.com/ads/library/..."
                  className="w-full bg-white/[0.03] border border-white/[0.07] rounded-lg px-3.5 py-2.5 text-[13px] text-white placeholder:text-[#333] focus:outline-none focus:border-white/[0.15] transition-colors"
                />
              </div>

              <div>
                <label className="text-[11px] text-[#555] uppercase tracking-widest font-medium mb-1.5 block">Tags (séparés par virgule)</label>
                <input
                  value={contentForm.tags}
                  onChange={(e) => updateContent("tags", e.target.value)}
                  placeholder="nike, video, sports"
                  className="w-full bg-white/[0.03] border border-white/[0.07] rounded-lg px-3.5 py-2.5 text-[13px] text-white placeholder:text-[#333] focus:outline-none focus:border-white/[0.15] transition-colors"
                />
              </div>

              {categories && categories.length > 0 && (
                <div>
                  <label className="text-[11px] text-[#555] uppercase tracking-widest font-medium mb-1.5 block">Catégorie</label>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => updateContent("category_id", "")}
                      className={`px-3 py-1.5 rounded-lg text-[12px] border transition-all ${!contentForm.category_id ? "border-white/[0.15] bg-white/[0.06] text-white" : "border-white/[0.06] text-[#555] hover:text-white"}`}
                    >
                      Aucune
                    </button>
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => updateContent("category_id", cat.id)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] border transition-all ${contentForm.category_id === cat.id ? "border-white/[0.15] bg-white/[0.06] text-white" : "border-white/[0.06] text-[#555] hover:text-white"}`}
                      >
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cat.color }} />
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="text-[11px] text-[#555] uppercase tracking-widest font-medium mb-1.5 block">Note initiale (optionnel)</label>
                <textarea
                  value={contentForm.initial_note}
                  onChange={(e) => updateContent("initial_note", e.target.value)}
                  placeholder="Ajoutez vos observations, idées ou commentaires sur ce contenu..."
                  rows={3}
                  className="w-full bg-white/[0.03] border border-white/[0.07] rounded-lg px-3.5 py-2.5 text-[13px] text-white placeholder:text-[#333] focus:outline-none focus:border-white/[0.15] transition-colors resize-none"
                  data-testid="initial-note-input"
                />
              </div>

              <button
                type="submit"
                disabled={saving || !contentForm.title.trim()}
                className="w-full bg-white text-black font-medium text-sm rounded-lg py-3 flex items-center justify-center gap-2 transition-all hover:shadow-[0_0_15px_rgba(255,255,255,0.08)] disabled:opacity-40"
                data-testid="save-content-btn"
              >
                {saving ? "Enregistrement..." : "Enregistrer"}
                {!saving && <Plus size={15} />}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default AddDropdown;
